import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import { Check, ChevronDown, Download, Eye, MessageSquarePlus, Paperclip, X } from "lucide-react";
import {
  baixarAnexo,
  mensagemDeErro,
  useAnexosChamado,
  useCriarInteracao,
  useDetalheChamado,
  useExecutarAcao,
} from "../chamados/gestao/api";
import { VisualizadorAnexo } from "../chamados/gestao/VisualizadorAnexo";
import type { AcaoDisponivel, AnexoChamado } from "../../types/portalCliente";
import { useMudarStatus, useStatusEquipe } from "./api";

/**
 * Ações rápidas no cartão do quadro (pedido de 05/10/2026), sem abrir o chamado: a etiqueta de
 * status muda o status (equipe T4i) ou executa a ação do cliente que vale naquela situação, e o
 * balão registra uma interação curta. Anexo, histórico e nota interna continuam no detalhe.
 *
 * Todo clique aqui para no cartão: sem isso, o clique abriria a janela do chamado.
 */

const parar = (e: { stopPropagation: () => void }) => e.stopPropagation();

/**
 * Caixa flutuante presa ao gatilho; fecha com clique fora ou Esc. Vai para o body (portal, posição
 * fixa): dentro do cartão ela ficava cortada pela rolagem da coluna do quadro. Ao rolar, acompanha o
 * gatilho — fechar na rolagem a derrubava no próprio clique, que rola o cartão para a vista.
 */
const Flutuante = ({
  aberto,
  ancora,
  aoFechar,
  largura = 288,
  children,
}: {
  aberto: boolean;
  ancora: React.RefObject<HTMLElement | null>;
  aoFechar: () => void;
  /** Em pixels; a caixa nunca passa da borda da tela. */
  largura?: number;
  children: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [posicao, setPosicao] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!aberto) return;
    const posicionar = () => {
      if (!ancora.current) return;
      const r = ancora.current.getBoundingClientRect();
      setPosicao({
        top: r.bottom + 4,
        left: Math.max(8, Math.min(r.left, window.innerWidth - largura - 8)),
      });
    };
    posicionar();
    window.addEventListener("scroll", posicionar, true);
    window.addEventListener("resize", posicionar);
    return () => {
      window.removeEventListener("scroll", posicionar, true);
      window.removeEventListener("resize", posicionar);
    };
  }, [aberto, ancora, largura]);

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (ref.current?.contains(alvo) || ancora.current?.contains(alvo)) return;
      aoFechar();
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") aoFechar();
    };
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("keydown", esc);
    };
  }, [aberto, aoFechar, ancora]);

  if (!aberto || !posicao) return null;
  return createPortal(
    <div
      ref={ref}
      onClick={parar}
      style={{ top: posicao.top, left: posicao.left, width: Math.min(largura, window.innerWidth - 16) }}
      className="fixed z-50 rounded-xl border border-gray-200 bg-white p-3 text-left shadow-xl"
    >
      {children}
    </div>,
    document.body
  );
};

/** A etiqueta de status do cartão vira o seletor: escolher, (observar) e aplicar. */
export const EtiquetaStatusEquipe = ({
  idChamado,
  idStatusAtual,
  nomeStatusAtual,
  className,
}: {
  idChamado: number;
  idStatusAtual: number;
  nomeStatusAtual: string;
  className: string;
}) => {
  const [aberto, setAberto] = useState(false);
  const gatilho = useRef<HTMLButtonElement>(null);
  const [escolhido, setEscolhido] = useState<number | null>(null);
  const [observacao, setObservacao] = useState("");
  const opcoes = useStatusEquipe(aberto);
  const mudar = useMudarStatus();

  const fechar = () => {
    setAberto(false);
    setEscolhido(null);
    setObservacao("");
    mudar.reset();
  };

  const aplicar = async () => {
    if (escolhido === null) return;
    try {
      await mudar.mutateAsync({ idChamado, idStatus: escolhido, observacao });
      fechar();
    } catch {
      // A mensagem fica na caixa, pelo estado da mutação.
    }
  };

  const nomeEscolhido = opcoes.data?.find((o) => o.id === escolhido)?.nome;

  return (
    <span className="inline-flex" onClick={parar}>
      <button
        ref={gatilho}
        type="button"
        onClick={() => (aberto ? fechar() : setAberto(true))}
        title="Mudar o status"
        aria-haspopup="listbox"
        aria-expanded={aberto}
        className={`${className} inline-flex items-center gap-1 hover:ring-2 hover:ring-blue-200`}
      >
        {nomeStatusAtual}
        <ChevronDown className="h-3 w-3 opacity-70" />
      </button>

      <Flutuante aberto={aberto} ancora={gatilho} aoFechar={fechar}>
        {escolhido === null ? (
          <>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Mudar status para</p>
            {opcoes.isLoading && <p className="text-sm text-gray-500">Carregando…</p>}
            <ul role="listbox" className="max-h-64 overflow-y-auto">
              {(opcoes.data ?? [])
                .filter((o) => o.id !== idStatusAtual)
                .map((o) => (
                  <li key={o.id}>
                    <button
                      type="button"
                      onClick={() => setEscolhido(o.id)}
                      className="w-full rounded-lg px-2 py-1.5 text-left text-sm text-gray-800 hover:bg-blue-50"
                    >
                      {o.nome}
                    </button>
                  </li>
                ))}
            </ul>
          </>
        ) : (
          <>
            <p className="mb-2 text-sm text-gray-800">
              {nomeStatusAtual} → <strong>{nomeEscolhido}</strong>
            </p>
            <textarea
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="Observação (opcional, o cliente vê)"
              maxLength={1900}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">Vai para a linha do tempo e por e-mail ao contato do chamado.</p>
            {mudar.isError && (
              <p className="mt-2 text-xs text-red-600">{mensagemDeErro(mudar.error, "Não foi possível mudar o status.")}</p>
            )}
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEscolhido(null)}
                className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={aplicar}
                disabled={mudar.isPending}
                className="inline-flex items-center gap-1 rounded-lg bg-blue-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-60"
              >
                <Check className="h-4 w-4" />
                {mudar.isPending ? "Aplicando…" : "Aplicar"}
              </button>
            </div>
          </>
        )}
      </Flutuante>
    </span>
  );
};

/**
 * A etiqueta de status para o cliente: as ações que valem na situação do chamado (cancelar,
 * aprovar, recusar, finalizar, reabrir). Quem decide quais valem é a API — o detalhe do chamado
 * traz a lista, buscada só quando a etiqueta abre.
 */
export const EtiquetaAcoesCliente = ({
  idChamado,
  nomeStatusAtual,
  className,
}: {
  idChamado: number;
  nomeStatusAtual: string;
  className: string;
}) => {
  const [aberto, setAberto] = useState(false);
  const gatilho = useRef<HTMLButtonElement>(null);
  const [escolhida, setEscolhida] = useState<AcaoDisponivel | null>(null);
  const [motivo, setMotivo] = useState("");
  const detalhe = useDetalheChamado(idChamado, aberto);
  const executar = useExecutarAcao();

  const fechar = () => {
    setAberto(false);
    setEscolhida(null);
    setMotivo("");
    executar.reset();
  };

  const confirmar = async () => {
    if (!escolhida) return;
    try {
      await executar.mutateAsync({ idChamado, acao: escolhida.acao, motivo: motivo.trim() || undefined });
      fechar();
    } catch {
      // A mensagem fica na caixa, pelo estado da mutação.
    }
  };

  const acoes = detalhe.data?.acoes ?? [];

  return (
    <span className="inline-flex" onClick={parar}>
      <button
        ref={gatilho}
        type="button"
        onClick={() => (aberto ? fechar() : setAberto(true))}
        title="Ações do chamado"
        aria-haspopup="menu"
        aria-expanded={aberto}
        className={`${className} inline-flex items-center gap-1 hover:ring-2 hover:ring-blue-200`}
      >
        {nomeStatusAtual}
        <ChevronDown className="h-3 w-3 opacity-70" />
      </button>

      <Flutuante aberto={aberto} ancora={gatilho} aoFechar={fechar}>
        {!escolhida ? (
          <>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Ações</p>
            {detalhe.isLoading && <p className="text-sm text-gray-500">Carregando…</p>}
            {!detalhe.isLoading && acoes.length === 0 && (
              <p className="text-sm text-gray-600">Nenhuma ação sua nesta situação. A T4i dá o próximo passo.</p>
            )}
            <ul role="menu">
              {acoes.map((a) => (
                <li key={a.acao}>
                  <button
                    type="button"
                    onClick={() => setEscolhida(a)}
                    className="w-full rounded-lg px-2 py-1.5 text-left text-sm text-gray-800 hover:bg-blue-50"
                  >
                    {a.acao}
                    {a.exigeMotivo && <span className="ml-1 text-xs text-gray-400">(com motivo)</span>}
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            <p className="mb-2 text-sm text-gray-800">
              <strong>{escolhida.acao}</strong> o chamado?
            </p>
            {escolhida.exigeMotivo && (
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Motivo (obrigatório)"
                maxLength={1900}
                rows={3}
                autoFocus
                className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
              />
            )}
            {executar.isError && (
              <p className="mt-2 text-xs text-red-600">{mensagemDeErro(executar.error, "Não foi possível concluir a ação.")}</p>
            )}
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEscolhida(null)}
                className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={confirmar}
                disabled={executar.isPending || (escolhida.exigeMotivo && !motivo.trim())}
                className="inline-flex items-center gap-1 rounded-lg bg-blue-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-60"
              >
                <Check className="h-4 w-4" />
                {executar.isPending ? "Enviando…" : escolhida.acao}
              </button>
            </div>
          </>
        )}
      </Flutuante>
    </span>
  );
};

/**
 * Interação curta pelo cartão, sem anexo: da equipe para o cliente, ou do cliente para a T4i. Sem
 * nota interna aqui (decisão de 05/10/2026) — ela fica no detalhe do chamado.
 */
export const RespostaRapida = ({ idChamado, equipe }: { idChamado: number; equipe: boolean }) => {
  const [aberto, setAberto] = useState(false);
  const gatilho = useRef<HTMLButtonElement>(null);
  const [texto, setTexto] = useState("");
  const criar = useCriarInteracao();

  const fechar = () => {
    setAberto(false);
    setTexto("");
    criar.reset();
  };

  const enviar = async () => {
    try {
      await criar.mutateAsync({ idChamado, comentario: texto.trim(), anexos: [] });
      fechar();
    } catch {
      // A mensagem fica na caixa, pelo estado da mutação.
    }
  };

  return (
    <span className="inline-flex" onClick={parar}>
      <button
        ref={gatilho}
        type="button"
        onClick={() => (aberto ? fechar() : setAberto(true))}
        title="Registrar uma interação"
        aria-label="Registrar uma interação"
        className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-2 py-1 text-slate-600 hover:border-blue-300 hover:text-blue-700"
      >
        <MessageSquarePlus className="h-3.5 w-3.5" />
      </button>

      <Flutuante aberto={aberto} ancora={gatilho} aoFechar={fechar} largura={384}>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Interação rápida</p>
          <button type="button" onClick={fechar} aria-label="Fechar" className="text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={equipe ? "Resposta ao cliente…" : "Mensagem para a T4i…"}
          maxLength={2000}
          rows={6}
          autoFocus
          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
        />
        {criar.isError && (
          <p className="mt-2 text-xs text-red-600">{mensagemDeErro(criar.error, "Não foi possível registrar.")}</p>
        )}
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={enviar}
            disabled={!texto.trim() || criar.isPending}
            className="rounded-lg bg-blue-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {criar.isPending ? "Enviando…" : equipe ? "Enviar ao cliente" : "Enviar para T4i"}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {equipe ? "Anexo, nota interna e histórico: abra o chamado." : "Anexo e histórico completo: abra o chamado."}
        </p>
      </Flutuante>
    </span>
  );
};

/** 1.234 KB, 2,5 MB. */
const tamanhoLegivel = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024)).toLocaleString("pt-BR")} KB`
    : `${(bytes / (1024 * 1024)).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;

/**
 * O clipe do cartão (07/10/2026): quantos anexos o chamado tem e, num clique, a lista deles sem abrir
 * o chamado. Imagem e PDF abrem no visualizador do detalhe; o resto baixa. A lista só é buscada
 * quando o clipe abre — o quadro já traz a contagem.
 */
export const AnexosRapidos = ({ idChamado, quantidade }: { idChamado: number; quantidade: number }) => {
  const [aberto, setAberto] = useState(false);
  const [visualizando, setVisualizando] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const gatilho = useRef<HTMLButtonElement>(null);
  const anexos = useAnexosChamado(idChamado, aberto || visualizando !== null);
  const exibiveis = (anexos.data ?? []).filter((a) => a.exibivelInline);

  const abrir = async (anexo: AnexoChamado) => {
    setErro(null);
    if (anexo.exibivelInline) {
      setVisualizando(exibiveis.findIndex((e) => e.id === anexo.id));
      setAberto(false);
      return;
    }
    try {
      await baixarAnexo(idChamado, anexo);
    } catch (e) {
      setErro(mensagemDeErro(e, "Não foi possível baixar o anexo."));
    }
  };

  return (
    <span className="inline-flex" onClick={parar}>
      <button
        ref={gatilho}
        type="button"
        onClick={() => setAberto((a) => !a)}
        title={quantidade > 0 ? `${quantidade} ${quantidade === 1 ? "anexo" : "anexos"}` : "Anexos"}
        aria-haspopup="dialog"
        aria-expanded={aberto}
        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 hover:border-blue-300 hover:text-blue-700"
      >
        <Paperclip className="h-3.5 w-3.5" />
        {quantidade > 0 && quantidade}
      </button>

      <Flutuante aberto={aberto} ancora={gatilho} aoFechar={() => setAberto(false)} largura={320}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Anexos</p>
        {anexos.isLoading && <p className="text-sm text-gray-500">Carregando…</p>}
        {anexos.isError && <p className="text-sm text-red-600">Não foi possível carregar os anexos.</p>}
        {anexos.data?.length === 0 && <p className="text-sm text-gray-500">Nenhum anexo.</p>}
        <ul className="max-h-72 space-y-0.5 overflow-y-auto">
          {(anexos.data ?? []).map((a) => (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => void abrir(a)}
                title={a.exibivelInline ? "Visualizar" : "Baixar"}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-gray-800 hover:bg-blue-50"
              >
                {a.exibivelInline ? (
                  <Eye className="h-4 w-4 shrink-0 text-blue-500" />
                ) : (
                  <Download className="h-4 w-4 shrink-0 text-gray-400" />
                )}
                <span className="min-w-0 flex-1 truncate">{a.denominacao ?? `Anexo ${a.id}`}</span>
                {a.interno && <span className="shrink-0 text-[10px] font-medium uppercase text-amber-700">interno</span>}
                <span className="shrink-0 text-xs text-gray-400">{tamanhoLegivel(a.tamanho)}</span>
              </button>
            </li>
          ))}
        </ul>
        {erro && <p className="mt-2 text-xs text-red-600">{erro}</p>}
      </Flutuante>

      {visualizando !== null && visualizando >= 0 && exibiveis.length > 0 && (
        <VisualizadorAnexo
          idChamado={idChamado}
          anexos={exibiveis}
          indiceInicial={visualizando}
          aoFechar={() => setVisualizando(null)}
        />
      )}
    </span>
  );
};
